import { RuntimeFeatureFlag } from '@/feature-flags/definitions'
import { useRuntimeFeatureFlag } from '@/feature-flags/useRuntimeFeatureFlag'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { CSSTransition } from 'react-transition-group'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import MatterBreadcrumbsMedChronAsset from '@/matter/components/matter-breadcrumbs/MatterBreadcrumbsMedChronAsset'
import ErrorComponent from '@/components/error/error-component'
import { Button } from '@/components/button/Button'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { useHubspotConversations } from '@/context/useHubspotConversations'
import { useViewportSizeInPixels } from '@/hooks/use-viewport-size-in-pixels'
import ContentViewerColumn from '@/content-viewer/components/ContentViewerColumn'
import ContentViewerSlideover from '@/content-viewer/components/ContentViewerSlideover'
import { ContentViewerActions } from '@/content-viewer/store/slice'
import { selectIsContentViewerOpen } from '@/content-viewer/store/selectors'
import { DEVICE_TYPE_BREAKPOINTS } from '@/hooks/useDeviceType'
import { CONTENT_VIEWER_COLUMN_TRANSITION_DURATION_MS } from '@/medical-common/constants'
import { AddDocuments, Show } from '@/assets/icons'
import MatterRecordReadOnlyNotice from '@/matter-asset-common/asset/MatterRecordReadOnlyNotice'
import { matterPaths } from '@/routing/paths'
import { formatLastActivityForCell } from '@/matter/utils/format-last-activity'
import { selectMatterById } from '@/matter/store/selectors'
import { canEditMedChron } from '@/matter/utils/matter-permissions'
import { MatterAssetFeatureProvider } from '@/matter-asset-common/feature/MatterAssetFeatureProvider'
import { getMedChronAssetTabName, kMedChronOwnerOnlyUpdateMessage, kMedChronEditorUpdateMessage, MedChronAssetTabs } from '../constants'
import { medChronFeatureConfig } from '../MedChronFeatureConfig'
import { MedChronAssetActions } from '../store/slice'
import { selectIsUpdateActive, selectMedChronAssetError, selectMedChronAssetForMatter, selectUpdateStatusForAsset } from '../store/selectors'
import { MedChronUpdateStatus } from '../store/schemas'
import MedChronAssetEmptyState from './MedChronAssetEmptyState'
import MedChronAssetBanners from './MedChronAssetBanners'
import MedChronAssetProcessingSkeleton from './MedChronAssetProcessingSkeleton'
import { MedChronVersionHistoryPopover } from './version-history/MedChronVersionHistoryPopover'

import { useMedChronEditingLease } from '../hooks/useMedChronEditingLease'
import EditingLeaseNotice from '@/editing-leases/components/EditingLeaseNotice'
import MedChronStartEditingButton from './MedChronStartEditingButton'
import { useAddFilesLeaseExpiredNotice } from '../hooks/useAddFilesLeaseExpiredNotice'
import MedChronLeaseExpiredNotice from './MedChronLeaseExpiredNotice'

const TABS = [
  { name: getMedChronAssetTabName(MedChronAssetTabs.ALL_EVENTS), path: MedChronAssetTabs.ALL_EVENTS, id: 'all_events_tab' },
  { name: getMedChronAssetTabName(MedChronAssetTabs.DETAILED_VIEW), path: MedChronAssetTabs.DETAILED_VIEW, id: 'detailed_view_tab' },
  { name: getMedChronAssetTabName(MedChronAssetTabs.SOURCE_FILES), path: MedChronAssetTabs.SOURCE_FILES, id: 'source_files_tab' },
]

/** The full editing explanation yields toolbar space in narrower content columns and split views. */
const kFullEditingNoticeMinWidth = 1024

/**
 * Layout for the matter's living Medical Chronology.
 *
 * Branches on the asset state: loading, error, empty (no chronology yet), first build in
 * flight (skeleton), or the tabbed chronology with status banners and the content viewer
 * split (column on desktop, slideover on mobile).
 */
export default function MedChronAssetLayout() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { toggleWidget } = useHubspotConversations()
  // Lifted so the failed-files banner's "Version info" action can open the version-history panel.
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false)

  const { width: viewportWidth } = useViewportSizeInPixels()
  const useColumnLayout = viewportWidth >= DEVICE_TYPE_BREAKPOINTS.DESKTOP

  const activeMatterId = useAppSelector((state: RootState) => state.mattersState.activeMatterId)
  const matterId = activeMatterId ?? undefined
  const leaseExpiredNotice = useAddFilesLeaseExpiredNotice(matterId)
  const asset = useAppSelector((state: RootState) => selectMedChronAssetForMatter(state, { matterId }))
  const assetError = useAppSelector((state: RootState) => selectMedChronAssetError(state, { matterId }))
  const isUpdateActive = useAppSelector((state: RootState) => selectIsUpdateActive(state, { matterId }))
  const updateStatus = useAppSelector((state: RootState) => selectUpdateStatusForAsset(state, { assetId: asset?.id }))
  const showContentViewer = useAppSelector((state: RootState) => selectIsContentViewerOpen(state))
  const matter = useAppSelector((state: RootState) => selectMatterById(state, { matterId: activeMatterId }))
  // Keep writes disabled while this selector is unresolved, even if the parent route has loaded.
  const { enabled: editorEnabled, loading: editorLoading } = useRuntimeFeatureFlag(RuntimeFeatureFlag.COLLAB_EDITOR_ROLE)
  const isReadOnly = !canEditMedChron(matter, editorEnabled && !editorLoading)
  const showReadOnlyNotice = isReadOnly && !!matter && !editorLoading
  const updateMessage = editorEnabled && !editorLoading ? kMedChronEditorUpdateMessage : kMedChronOwnerOnlyUpdateMessage

  const lease = useMedChronEditingLease()
  const leaseReadOnly = !isReadOnly && !lease.canWrite && !lease.initializing

  const referenceColumnRef = useRef<HTMLDivElement>(null)
  const tabHeaderRef = useRef<HTMLDivElement>(null)
  const tabListRef = useRef<HTMLDivElement>(null)
  const singleRowTabHeaderWidthRef = useRef(0)
  const [isTabHeaderMultiRow, setIsTabHeaderMultiRow] = useState(false)
  const [isEditingNoticeCompact, setIsEditingNoticeCompact] = useState(true)

  // The first build has no live events yet - show the skeleton until it publishes
  const isFirstBuildInFlight = Boolean(asset && asset.chronology_version === 0 && isUpdateActive)
  // The first build errored with nothing published - a full error state replaces the empty table
  const isFirstBuildError = Boolean(asset && asset.chronology_version === 0 && !isUpdateActive && updateStatus?.update.status === MedChronUpdateStatus.ERROR)

  // Clear content viewer when component unmounts
  useEffect(() => {
    return () => {
      dispatch(ContentViewerActions.clearContentViewer())
    }
  }, [dispatch])

  useLayoutEffect(() => {
    const tabHeader = tabHeaderRef.current
    const tabList = tabListRef.current
    if (!tabHeader || !tabList) return

    const updateLayout = () => {
      if (tabHeader.clientWidth === 0) return

      const compact = tabHeader.clientWidth < kFullEditingNoticeMinWidth
      if (compact !== isEditingNoticeCompact) {
        setIsEditingNoticeCompact(compact)
        if (leaseReadOnly) {
          singleRowTabHeaderWidthRef.current = 0
          setIsTabHeaderMultiRow(false)
          return
        }
      }

      if (isTabHeaderMultiRow) {
        if (tabHeader.clientWidth >= singleRowTabHeaderWidthRef.current) {
          setIsTabHeaderMultiRow(false)
        }
        return
      }

      const tabOverflowWidth = tabList.scrollWidth - tabList.clientWidth
      if (tabOverflowWidth > 1) {
        singleRowTabHeaderWidthRef.current = tabHeader.clientWidth + tabOverflowWidth
        setIsTabHeaderMultiRow(true)
      }
    }

    updateLayout()
    const resizeObserver = new ResizeObserver(updateLayout)
    resizeObserver.observe(tabHeader)
    resizeObserver.observe(tabList)
    return () => resizeObserver.disconnect()
  }, [isReadOnly, leaseReadOnly, lease.lease?.holder_display_name, isTabHeaderMultiRow, isEditingNoticeCompact, asset?.last_built_at])

  // Read-only collaborators get the "Viewing" pill instead of this button, so it only renders for editors.
  const addFilesButton = (
    <Button
      onClick={() => matterId && navigate(matterPaths.chronology.addFiles(matterId))}
      disabled={isUpdateActive || !lease.canWrite}
      variant="tonal"
      color="neutral"
      size="sm"
      leadingIcon={<AddDocuments />}
      className="disabled:gap-1 disabled:px-3 disabled:ring-0"
      title={isUpdateActive ? 'An update is already in progress' : undefined}
    >
      Add Files
    </Button>
  )

  return (
    <div className="grow flex flex-col w-full h-full overflow-hidden">
      <MatterBreadcrumbsMedChronAsset />

      {/* Loading the overview for the first time */}
      {asset === undefined && !assetError && (
        <div className="flex-1 flex items-center justify-center">
          <CircularProgressContinuousSized size={20} thickness={7} />
        </div>
      )}

      {/* Overview failed to load */}
      {asset === undefined && assetError && (
        <ErrorComponent
          code="500"
          title="Error Loading Medical Chronology"
          message={
            <>
              {'Our servers ran into a problem. Please '}
              <button
                onClick={() => matterId && dispatch(MedChronAssetActions.fetchAssetForMatter({ matterId }))}
                className="text-brand-500 hover:text-brand-400 underline cursor-pointer"
              >
                try again
              </button>
              {', or '}
              <button onClick={toggleWidget} className="text-brand-500 hover:text-brand-400 underline cursor-pointer">
                contact support
              </button>
              {' if the issue continues.'}
            </>
          }
        />
      )}

      {/* No chronology yet */}
      {asset === null && matterId && (
        <>
          {leaseExpiredNotice && (
            <div className="pt-4 px-4">
              <MedChronLeaseExpiredNotice matterId={matterId} canResume={!isReadOnly} />
            </div>
          )}
          <MedChronAssetEmptyState matterId={matterId} />
        </>
      )}

      {/* The living chronology */}
      {asset && matterId && (
        <MatterAssetFeatureProvider config={medChronFeatureConfig} storeKey={asset.id}>
          <div className="flex flex-1 overflow-hidden">
            {/* Left: banners + tabbed content area */}
            <div
              className={clsx(
                'flex flex-col max-w-none pt-2 lg:pt-3.5 lg:pl-2 transition-[width] duration-200 ease-out',
                '[container-type:inline-size]',
                showContentViewer && useColumnLayout ? 'w-1/2' : 'w-full'
              )}
            >
              {leaseExpiredNotice && (
                <div className={clsx('mb-2 mr-2', isTabHeaderMultiRow ? 'ml-2' : 'ml-1 lg:ml-0')}>
                  <MedChronLeaseExpiredNotice matterId={matterId} canResume={!isReadOnly} />
                </div>
              )}
              <MedChronAssetBanners
                matterId={matterId}
                assetId={asset.id}
                isTabHeaderMultiRow={isTabHeaderMultiRow}
                onShowVersionInfo={() => setVersionHistoryOpen(true)}
              />

              {/* Overflow viewing badge — sits above the tab row when tabs wrap to multi-row so they keep the full row width. Mirrors the desktop pill's !isTabHeaderMultiRow condition. */}
              {showReadOnlyNotice && isTabHeaderMultiRow && (
                <div className="ml-2 mr-2">
                  <MatterRecordReadOnlyNotice icon={<Show />} headline="Viewing." message={updateMessage} />
                </div>
              )}

              <div
                ref={tabHeaderRef}
                className={clsx(
                  'grow-0 shrink-0 flex relative z-10 -mb-px lg:[&:first-child>[data-asset-actions]]:mt-[-6px]',
                  isTabHeaderMultiRow
                    ? 'flex-col-reverse items-stretch gap-3 pl-0 pt-1.5 first:pt-3 max-md:[&:first-child>[data-asset-actions]]:mt-[-6px]'
                    : 'items-end gap-1 pl-1 lg:pl-0'
                )}
              >
                {/* When the single row would overflow, the actions move above the tabs. */}
                <div
                  ref={tabListRef}
                  className={clsx(
                    'flex items-end gap-1 overflow-x-auto',
                    '[&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]',
                    isTabHeaderMultiRow ? 'w-full flex-none px-2' : 'flex-1 min-w-0'
                  )}
                >
                  {TABS.map((tab) => (
                    <NavLink
                      key={tab.path}
                      to={tab.path}
                      id={tab.id}
                      className={({ isActive }) =>
                        clsx(
                          'relative px-3 py-1 flex items-center gap-1 text-sm font-semibold tracking-[-0.15px]',
                          'transition-all duration-150 ease-in-out rounded-t-md border whitespace-nowrap',
                          isTabHeaderMultiRow && 'flex-1 justify-center',
                          isActive
                            ? 'bg-brand-neutral-50 text-brand-800 border-b-transparent'
                            : 'bg-brand-neutral-100 text-brand-neutral-700 hover:bg-brand-neutral-200'
                        )
                      }
                    >
                      {tab.name}
                    </NavLink>
                  ))}
                </div>

                {/* ResizeObserver reacts both to viewport changes and to the content viewer shrinking this column. */}
                <div
                  data-asset-actions
                  className={clsx('shrink-0 flex items-center gap-3 md:mt-0', isTabHeaderMultiRow ? 'w-full justify-between px-3 pb-0' : 'pl-2 pr-2 pb-2')}
                >
                  {asset.last_built_at && (
                    <div className={clsx('flex shrink-0 items-center gap-3', isTabHeaderMultiRow && !leaseReadOnly && 'order-2')}>
                      <span className="text-xs font-medium text-brand-neutral-600 whitespace-nowrap">{formatLastActivityForCell(asset.last_built_at)}</span>
                      <span className={clsx('h-6 w-px bg-brand-neutral-200', isTabHeaderMultiRow ? 'hidden' : 'block')} aria-hidden />
                    </div>
                  )}
                  <div className={clsx('flex items-center gap-3', leaseReadOnly ? 'min-w-0' : 'shrink-0', isTabHeaderMultiRow && !leaseReadOnly && 'order-1')}>
                    {leaseReadOnly ? (
                      <>
                        <EditingLeaseNotice lease={lease.lease} compact={isEditingNoticeCompact} />
                        <MedChronStartEditingButton />
                      </>
                    ) : isReadOnly ? (
                      // Read-only collaborators can't add files or roll back versions — a self-explanatory
                      // "Viewing" pill replaces both actions (Figma node 15052:54797).
                      showReadOnlyNotice &&
                      !isTabHeaderMultiRow && <MatterRecordReadOnlyNotice icon={<Show />} shape="pill" headline="Viewing." message={updateMessage} />
                    ) : (
                      <>
                        {addFilesButton}
                        <MedChronVersionHistoryPopover
                          assetId={asset.id}
                          disabled={isUpdateActive || !lease.canWrite}
                          open={versionHistoryOpen}
                          onOpenChange={setVersionHistoryOpen}
                        />
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="overflow-hidden flex-1 min-h-0 flex flex-col lg:border-l border-t bg-brand-neutral-50">
                <div className="flex flex-col flex-1 h-full">
                  {isFirstBuildInFlight ? (
                    <MedChronAssetProcessingSkeleton />
                  ) : isFirstBuildError && updateStatus ? (
                    <ErrorComponent
                      code="500"
                      title="Medical Chronology Failed to Generate"
                      message={
                        <>
                          {'Something went wrong while building the chronology. '}
                          {lease.canWrite ? 'Please try again or ' : 'Please '}
                          <button onClick={toggleWidget} className="text-brand-500 hover:text-brand-400 underline cursor-pointer">
                            contact support
                          </button>
                          {' with this ID if the issue continues: '}
                          <span className="font-mono">{updateStatus.update.id}</span>
                          {'.'}
                        </>
                      }
                      customLink={!lease.canWrite ? undefined : { label: 'Try Again', to: matterPaths.chronology.addFiles(matterId) }}
                    />
                  ) : (
                    <Outlet />
                  )}
                </div>
              </div>
            </div>

            {/* Right: Content Viewer column */}
            <CSSTransition
              appear
              nodeRef={referenceColumnRef}
              in={showContentViewer}
              timeout={{ enter: CONTENT_VIEWER_COLUMN_TRANSITION_DURATION_MS, exit: 0 }}
              classNames="content-viewer-column"
              unmountOnExit
            >
              <div
                ref={referenceColumnRef}
                className={clsx('overflow-hidden flex flex-col h-full transform-gpu will-change-transform', useColumnLayout ? 'w-1/2' : 'w-0')}
              >
                <ContentViewerColumn conversationId="none" />
              </div>
            </CSSTransition>

            {/* Content viewer as slideover - small screens */}
            {!useColumnLayout && <ContentViewerSlideover open={showContentViewer} conversationId="none" />}
          </div>
        </MatterAssetFeatureProvider>
      )}
    </div>
  )
}
